import { defineSetting } from "idfweb-settings";

/**
 * The page's theme: light or dark, following the system unless the button
 * has said otherwise.
 *
 * The page owns this and the workbench, in the same document, follows it
 * through `color-scheme`.
 * The choice is a setting, also in the settings tab. A choice made with the
 * button that agrees with the system clears it, so pressing the button until
 * the page matches the system is also how it gets back to following the system.
 */
export type Theme = "light" | "dark";

/** What is stored: a deviation from the system, or nothing. */
export type Preference = Theme | "system";

const preferenceSetting = defineSetting({
    key: "idfweb.theme",
    type: "enum",
    title: "Color Theme",
    description: "Light or dark, or the one the system uses.",
    group: ["Appearance"],
    default: "system",
    options: [
        { value: "system", label: "System" },
        { value: "light", label: "Light" },
        { value: "dark", label: "Dark" },
    ],
});

export function effectiveTheme(preference: Preference, systemDark: boolean): Theme {
    if (preference === "system") return systemDark ? "dark" : "light";
    return preference;
}

export function preferenceFor(wanted: Theme, systemDark: boolean): Preference {
    return effectiveTheme("system", systemDark) === wanted ? "system" : wanted;
}

export interface ThemeController {
    readonly theme: Theme;
    set(theme: Theme): void;
    toggle(): void;
    onChange(listener: (theme: Theme) => void): void;
}

/** Apply the theme to the document and keep it applied as the system changes. */
export function installTheme(): ThemeController {
    const system = window.matchMedia("(prefers-color-scheme: dark)");
    const listeners = new Set<(theme: Theme) => void>();
    let preference = preferenceSetting.get();
    let theme = effectiveTheme(preference, system.matches);

    const apply = (): void => {
        const next = effectiveTheme(preference, system.matches);
        document.documentElement.dataset["theme"] = next;
        if (next === theme) return;
        theme = next;
        for (const listener of listeners) listener(theme);
    };
    apply();
    system.addEventListener("change", apply);
    preferenceSetting.onChange((value) => {
        preference = value;
        apply();
    });

    return {
        get theme() {
            return theme;
        },
        set(wanted: Theme): void {
            preference = preferenceFor(wanted, system.matches);
            preferenceSetting.set(preference);
            apply();
        },
        toggle(): void {
            this.set(theme === "dark" ? "light" : "dark");
        },
        onChange(listener: (theme: Theme) => void): void {
            listeners.add(listener);
        },
    };
}

/**
 * The button that switches the theme, showing the one it would switch to.
 *
 * Styled as the device pane's buttons, which share the page's toolbar with
 * it — see main.ts.
 */
export function createThemeButton(theme: ThemeController): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "idfweb-device-button idfweb-icon-button theme-toggle";
    button.title = "Switch between light and dark";
    button.innerHTML = `
        <svg class="sun" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="8" cy="8" r="3" />
            <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.4 1.4M11.6 11.6 13 13M3 13l1.4-1.4M11.6 4.4 13 3" />
        </svg>
        <svg class="moon" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M13.5 9.5A6 6 0 0 1 6.5 2.5a6 6 0 1 0 7 7Z" />
        </svg>`;
    button.addEventListener("click", () => theme.toggle());
    return button;
}
